import { Box, Flex, Image, Skeleton, Text } from "@chakra-ui/react";

import { ArtistContentCardProps } from "../../types";
import { ContentCardActions } from "./components/ContentCardActions";
import { ContentCardFooter } from "./components/ContentCardFooter";
import { CountryFlag } from "./components/CountryFlag";
import { useGridCardDimensions } from "./hooks/useGridCardDimensions";
import { useMobileBreakpoint } from "../../utils/useBreakpoints";

export function ArtistListCard({
  content,
  layout: { display, style },
  fullWidth,
  onToggleFollow,
}: ArtistContentCardProps) {
  const {
    include_cover_image,
    include_name,
    include_total_favorites,
    include_total_releases,
    include_last_updated,
    include_follow,
    include_title,
    include_footer = true,
  } = display;
  const { size: layout_size } = style;
  const isSmall = layout_size === "s";

  const isMobile = useMobileBreakpoint();

  const { width, height } = useGridCardDimensions(
    "creators",
    style.mode,
    layout_size,
  );

  if (layout_size === "v2") {
    return (
      <Flex
        w="full"
        h="100px"
        align="center"
        gap="12px"
        pr="12px"
        borderRadius="8px"
        overflow="hidden"
      >
        {include_cover_image && (
          <Image
            src={content.avatar_url}
            h="80px"
            w="80px"
            minW="80px"
            objectFit="cover"
            borderRadius="20px"
            alt={`Avatar for ${content.roman_name}`}
            fallback={<Skeleton h="80px" w="80px" minW="80px" borderRadius="20px" />}
          />
        )}
        <Flex direction="column" flex={1} minW={0} justify="center" gap="2px">
          {include_name && (
            <Text fontFamily="Roboto" fontSize="14px" fontWeight="500" color="white" lineHeight="1.3" noOfLines={1}>
              {content.roman_name}
            </Text>
          )}
          {include_title && content.type_display && (
            <Text fontFamily="Roboto" fontSize="12px" fontWeight="400" color="charcoal.200" lineHeight="1.3" noOfLines={1}>
              {content.type_display}
            </Text>
          )}
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex width={fullWidth ? "100%" : width} pl={isMobile ? "8px" : "16px"}>
      <Flex
        width={fullWidth ? "100%" : `${width}px`}
        height={`${height}px`}
        direction="row"
        background="charcoal.900"
        position="relative"
        alignItems="center"
        justifyContent="space-between"
        pr="1rem"
        borderRadius={8}
        py="8px"
        gap={isMobile ? 1 : 2}
      >
        <Box
          left={isSmall ? -2 : -4}
          height="100%"
          position="relative"
          aspectRatio={1}
        >
          {include_cover_image ? (
            <Image
              src={content.avatar_url}
              width="100%"
              height="100%"
              position="relative"
              borderRadius="20px"
              alt={`Cover image for ${content.roman_name}`}
              fallback={
                <Skeleton width="100%" height="100%" borderRadius="20px" />
              }
            />
          ) : null}
        </Box>
        <Flex
          direction="row"
          position="relative"
          alignItems="center"
          height="100%"
          overflow="hidden"
          minW={0}
          flex={1}
        >
          <Flex
            direction="column"
            w="full"
            gap={2}
            justifyContent="space-between"
            pr="1rem"
            overflow="hidden"
            minW={0}
          >
            <Flex direction="column" paddingTop={isMobile ? 2 : 0} gap={1}>
              {include_name ? (
                <Text
                  variant={isSmall ? "cardSubtitle" : "cardHeader"}
                  noOfLines={1}
                >
                  {content.roman_name}
                </Text>
              ) : null}
              {include_title ? (
                <Flex direction="row" alignItems="center" gap={2}>
                  {!isSmall ? (
                    <CountryFlag countryId={content.country_id} />
                  ) : null}
                  <Text
                    variant={isSmall ? "cardVitals" : "cardSubtitle"}
                    color="neutral.200"
                    noOfLines={1}
                  >
                    {content.type_display}
                  </Text>
                </Flex>
              ) : null}
            </Flex>
            <Flex alignSelf="flex-start">
              {include_footer && !isSmall ? (
                <ContentCardFooter
                  total_favorites={content.total_favorites}
                  total_projects={content.total_projects}
                  total_releases={content.total_releases}
                  update_time={content.last_activity}
                  include_last_updated={include_last_updated}
                  include_total_favorites={include_total_favorites}
                  include_total_releases={include_total_releases}
                  include_total_projects={true}
                  layoutMode="list"
                  useAlternateFavoritesIcon
                />
              ) : null}
            </Flex>
          </Flex>
        </Flex>
        <ContentCardActions
          showFollow={(!isSmall && include_follow) ?? false}
          onToggleFollow={onToggleFollow}
          isFollowing={content.user_is_following}
        />
      </Flex>
    </Flex>
  );
}
